import { Dexie, liveQuery, type EntityTable, type DexieError } from 'dexie';
import type { MessagePayload } from 'firebase/messaging';
import { useObservable } from '@vueuse/rxjs';
import { defineStore } from 'pinia';
import { computed } from 'vue';

export interface MessageItem extends MessagePayload {
	read: boolean,
	received: Date,
	noAutoclear: boolean
}

export const db = new Dexie('msgdb') as Dexie & {
	msgs: EntityTable<MessageItem, 'messageId'>
}

db.version(1).stores({
	msgs: 'messageId, received, read'
});

export async function recordMessage(message: MessagePayload, noAutoclear: boolean = false) {
	try {
		await db.msgs.add({
			... message,
			received: new Date(),
			read: false,
			noAutoclear
		}, message.messageId);
	} catch (e) {
		if(e instanceof Dexie.ConstraintError) {
			// no-op, it's handled
		} else {
			throw e;
		}
	}
}

export async function markMessageRead(messageId: string) {
	await db.msgs.update(messageId, { read: true });
}

export async function clearAllNotifications() {
	await db.msgs.clear();
}

const ONE_WEEK = 6.048e+8;
export async function clearOldNotifications() {
	const aWeekAgo = new Date(Date.now() - ONE_WEEK);

	const oldMsgs = await db.msgs.where('received').belowOrEqual(aWeekAgo).toArray();
	await db.msgs.bulkDelete(oldMsgs.map(msg => msg.messageId));
}

export const useNotificationDB = defineStore('notification-db', () => {

	// @ts-expect-error the types are mismatched, don't worry about it
	const notifications = useObservable<MessageItem[]>(liveQuery(() => db.msgs.orderBy('received').reverse().toArray()));

	const unread = computed(() => notifications.value?.filter(item => !item.read) ?? []);

	async function markAllRead() {
		await Promise.all(unread.value.map(async n => {
			if(!n.noAutoclear) {
				await markMessageRead(n.messageId)
			}
		}));
	}

	return { notifications, unread, markAllRead };

});